import React, { useEffect, useRef, useState } from "react";
import Slider from "react-slick";
import tw from "twin.macro";
import styled from "styled-components";
import { SectionHeading } from "components/misc/Headings";
import { PrimaryButton as PrimaryButtonBase } from "components/misc/Buttons";
import PortalApi from "api/PortalApi";
import { ChevronLeft, ChevronRight } from "react-feather";

// Types
interface LinkedImageCard {
  id: number;
  imageSrc: string;
  title: string;
  link: string;
  openInNewWindow?: boolean;
}

// Styled components
const Container = tw.div`relative`;
const Content = tw.div`max-w-screen-xl mx-auto`;

const HeadingWithControl = tw.div`flex flex-col items-center sm:items-stretch sm:flex-row justify-between pt-2 pb-8`;
const Heading = tw(SectionHeading)``;
const Controls = tw.div`flex items-center`;

const ControlButton = styled(PrimaryButtonBase)`
  ${tw`first:ml-0 ml-6 rounded-full `}
  svg {
    ${tw`w-6 h-6`}
  }
`;
const PrevButton = tw(ControlButton)``;
const NextButton = tw(ControlButton)``;

const CardSlider = styled(Slider)`
  ${tw`mt-16`}

  .slick-track {
    ${tw`flex m-2`}
  }

  .slick-list {
    ${tw`overflow-visible`}
  }

  .slick-slide {
    ${tw`h-auto block m-5 justify-center mb-1`}
  }
`;

const Card = styled.a`
  ${tw`
    flex! flex-col h-full w-full 
    sm:border sm:rounded-tl-4xl sm:rounded-br-5xl 
    relative focus:outline-none
    bg-primary-200 text-black
    cursor-pointer
    transition duration-300
    hover:bg-primary-300
  `}

  &:hover {
    transform: scale(1.05);
  }
`;

const CardImage = styled.div<{ imageSrc: string }>`
  background-image: url("${(props) => props.imageSrc}");
  ${tw`w-full h-56 sm:h-64 bg-cover bg-center rounded sm:rounded-none sm:rounded-tl-4xl`}
  transition: transform 0.3s ease;
`;

const TextInfo = tw.div`py-6 sm:px-10 sm:py-6`;
const TitleReviewContainer = tw.div`flex flex-col sm:flex-row sm:justify-between sm:items-center`;
const Title = tw.h5`text-2xl font-bold`;

// Component
const HomePageSlider: React.FC = () => {
  const sliderRef = useRef<Slider>(null);
  const [linkedImageCards, setLinkedImageCards] = useState<LinkedImageCard[]>([]);

  const sliderSettings = {
    arrows: false,
    slidesToShow: 4,
    responsive: [
      {
        breakpoint: 1280,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 900,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  useEffect(() => {
    const fetchData = async () => {
      const response = await PortalApi.getHomePageData();
      if (response && response.homePageImageLinkList) {
        const mappedItems: LinkedImageCard[] = response.homePageImageLinkList.map((item: any) => ({
          id: item.homePageImageLinkId,
          imageSrc: item.imageUrl,
          title: item.header,
          link: item.redirectUrl,
          openInNewWindow: item.openInNewWindow,
        }));
        setLinkedImageCards(mappedItems);
      } else {
        setLinkedImageCards([]);
      }
    };

    fetchData();
  }, []);

  return (
    <Container>
      <Content>
        <CardSlider ref={sliderRef} {...sliderSettings}>
          {Array.isArray(linkedImageCards) &&
            linkedImageCards.map((card, index) => (
              <Card key={index} href={card.link} target={card.openInNewWindow ? "_blank" : "_self"}>
                <CardImage className="cardImage" imageSrc={card.imageSrc} />
                <TextInfo>
                  <TitleReviewContainer>
                    <Title>{card.title}</Title>
                  </TitleReviewContainer>
                </TextInfo>
              </Card>
            ))}
        </CardSlider>

        <HeadingWithControl>
          <Heading></Heading>
          <Controls>
            <PrevButton onClick={() => sliderRef.current?.slickPrev()}>
              <ChevronLeft />
            </PrevButton>
            <NextButton onClick={() => sliderRef.current?.slickNext()}>
              <ChevronRight />
            </NextButton>
          </Controls>
          <Heading></Heading>
        </HeadingWithControl>
      </Content>
    </Container>
  );
};

export default HomePageSlider;
